/**
 * ReaderFLEX Book Library — catalog data.
 *
 * ---------------------------------------------------------------------------
 * HOW TO ADD / EDIT / FEATURE A BOOK
 * ---------------------------------------------------------------------------
 * 1. Add or edit a record in the BOOKS array below.
 * 2. To make a title LIVE (enable its Amazon CTA), you MUST:
 *      - Set status: 'published'
 *      - Provide a manually VERIFIED amazonAsin (10-char ASIN from the exact
 *        product-detail page and edition shown).
 *    If amazonAsin is missing/unverified the purchase CTA stays disabled and
 *    the record is treated as "needs verification". This is intentional and
 *    compliant. Do NOT guess ASINs.
 * 3. To feature a ReaderFLEX title, set isFeatured: true and a featuredRank.
 * 4. Covers:
 *      - ReaderFLEX-owned covers: place a local asset in /public/books and set
 *        coverSrc to e.g. '/books/read-faster-cover.jpg'.
 *      - Third-party covers: only use a properly licensed asset or an
 *        authorized Amazon Associates method. Do NOT download arbitrary
 *        copyrighted images. When no licensed cover is available, leave
 *        coverSrc empty ('') and an editorial text placeholder is rendered.
 *
 * TYPE REFERENCE (documented for clarity; this is a plain JS project):
 *
 *   BookStatus   = 'published' | 'coming-soon' | 'unavailable'
 *   BookCategory = 'readerflex-original' | 'speed-reading' | 'comprehension'
 *                | 'memory' | 'focus' | 'learning' | 'note-taking'
 *                | 'reading-science'
 *
 *   BookRecord = {
 *     id, slug, title, subtitle?, author, publisher?, description, bestFor,
 *     readerflexNote?, categories[], audienceTags?[], badges?[],
 *     coverSrc, coverAlt, status, isReaderFlexTitle, isFeatured, featuredRank?,
 *     amazonAsin?, amazonMarketplace?, amazonProductUrl?, amazonReviewUrl?,
 *     relatedArticleUrl?, sampleUrl?, learnMoreUrl?, publicationYear?, editionLabel?
 *   }
 * ---------------------------------------------------------------------------
 */

export const CATEGORIES = [
    { id: 'all', label: 'All Books' },
    { id: 'readerflex-original', label: 'ReaderFLEX Originals' },
    { id: 'speed-reading', label: 'Speed Reading' },
    { id: 'comprehension', label: 'Comprehension' },
    { id: 'memory', label: 'Memory' },
    { id: 'focus', label: 'Focus' },
    { id: 'learning', label: 'Learning' },
    { id: 'note-taking', label: 'Note-Taking' },
    { id: 'reading-science', label: 'Reading Science' },
];

export const CATEGORY_LABELS = CATEGORIES.reduce((acc, c) => {
    acc[c.id] = c.label;
    return acc;
}, {});

export const AUDIENCE_TAGS = [
    'Beginners',
    'Students',
    'Professionals',
    'Lifelong Learners',
    'Writers',
    'Research-Minded Readers',
];

export const BOOKS = [
    // =====================================================================
    // ReaderFLEX Originals
    // =====================================================================
    {
        id: 'rf-read-faster',
        slug: 'read-faster-without-forgetting-everything',
        title: 'Read Faster Without Forgetting Everything',
        subtitle: 'The ReaderFLEX Method for Speed, Focus, and Comprehension',
        author: 'ReaderFLEX',
        publisher: 'ReaderFLEX Publishing',
        description:
            'A practical guide to reading with more speed, focus, and retention without sacrificing understanding. Built around the ReaderFLEX approach of controlled speed, guided attention, and simple retention habits you can actually keep.',
        bestFor: 'Readers who want to read faster while still remembering and using what they read.',
        readerflexNote:
            'The foundational ReaderFLEX guide. It connects every part of the method: pacing, attention, comprehension, and retention.',
        categories: ['readerflex-original', 'speed-reading', 'comprehension'],
        audienceTags: ['Beginners', 'Professionals', 'Lifelong Learners'],
        badges: ['ReaderFLEX Original'],
        coverSrc: '/books/read-faster-without-forgetting-everything.png',
        coverAlt:
            'Cover of Read Faster Without Forgetting Everything, the ReaderFLEX Method for Speed, Focus, and Comprehension',
        status: 'published',
        isReaderFlexTitle: true,
        isFeatured: true,
        featuredRank: 1,
        amazonAsin: 'B0H9PF5PT6',
        amazonMarketplace: 'US',
        amazonReviewUrl: '/review/read-faster-without-forgetting-everything-ebook',
        learnMoreUrl: '/books/read-faster-without-forgetting-everything',
        reviewFormats: ['ebook', 'paperback', 'hardcover'],
        coverFit: 'contain',
        editionLabel: 'First Edition',
    },

    // =====================================================================
    // Speed reading and reading efficiency
    // =====================================================================
    {
        id: 'breakthrough-rapid-reading',
        slug: 'breakthrough-rapid-reading',
        title: 'Breakthrough Rapid Reading',
        author: 'Peter Kump',
        description:
            'A structured, exercise-driven program for building reading speed through deliberate practice. Selected for its methodical, drill-based approach that treats faster reading as a trainable skill.',
        bestFor: 'Readers who want a step-by-step practice program rather than quick tips.',
        categories: ['speed-reading'],
        audienceTags: ['Beginners', 'Lifelong Learners'],
        badges: ['Practical Classic'],
        coverSrc: '',
        coverAlt: 'Cover of Breakthrough Rapid Reading by Peter Kump',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: '10-days-faster-reading',
        slug: '10-days-to-faster-reading',
        title: '10 Days to Faster Reading',
        author: 'Abby Marks-Beale',
        description:
            'An accessible introduction to reading efficiency, covering common habits that slow readers down and simple techniques to move through text more comfortably.',
        bestFor: 'Beginners who want a gentle, approachable starting point.',
        categories: ['speed-reading'],
        audienceTags: ['Beginners'],
        badges: ['Beginner-Friendly', 'Accessible Introduction'],
        coverSrc: '',
        coverAlt: 'Cover of 10 Days to Faster Reading by Abby Marks-Beale',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'speed-reading-buzan',
        slug: 'speed-reading-tony-buzan',
        title: 'Speed Reading',
        author: 'Tony Buzan',
        description:
            'A widely regarded introduction to speed-reading fundamentals from the author known for mind mapping. Recommended for readers interested in classic techniques for widening focus and reducing backtracking.',
        bestFor: 'Readers curious about foundational speed-reading concepts.',
        categories: ['speed-reading'],
        audienceTags: ['Beginners', 'Lifelong Learners'],
        badges: ['Influential Classic'],
        coverSrc: '',
        coverAlt: 'Cover of Speed Reading by Tony Buzan',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'read-better-and-faster',
        slug: 'how-to-read-better-and-faster',
        title: 'How to Read Better and Faster',
        author: 'Norman Lewis',
        description:
            'A long-standing guide pairing reading efficiency with vocabulary and comprehension development. Selected for its combined focus on speed and understanding.',
        bestFor: 'Readers who want speed and comprehension addressed together.',
        categories: ['speed-reading', 'comprehension'],
        audienceTags: ['Students', 'Lifelong Learners'],
        badges: ['Influential Classic'],
        coverSrc: '',
        coverAlt: 'Cover of How to Read Better and Faster by Norman Lewis',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'limitless-kwik',
        slug: 'limitless-jim-kwik',
        title: 'Limitless',
        author: 'Jim Kwik',
        description:
            'A popular guide to learning, memory, and reading habits framed around mindset, motivation, and method. Recommended for readers who want an energizing, big-picture take on upgrading how they learn.',
        bestFor: 'Readers looking for motivation alongside practical technique.',
        categories: ['speed-reading', 'learning', 'memory'],
        audienceTags: ['Beginners', 'Lifelong Learners', 'Professionals'],
        badges: ['Accessible Introduction'],
        coverSrc: '',
        coverAlt: 'Cover of Limitless by Jim Kwik',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },

    // =====================================================================
    // Comprehension and intentional reading
    // =====================================================================
    {
        id: 'how-to-read-a-book',
        slug: 'how-to-read-a-book',
        title: 'How to Read a Book',
        author: 'Mortimer J. Adler and Charles Van Doren',
        description:
            'A definitive guide to reading at different levels, from inspectional reading to analytical and comparative reading. Widely regarded as essential for reading with intention and depth.',
        bestFor: 'Readers who want a framework for reading demanding books well.',
        categories: ['comprehension'],
        audienceTags: ['Students', 'Research-Minded Readers', 'Lifelong Learners'],
        badges: ['Influential Classic', 'Deep Reading'],
        coverSrc: '',
        coverAlt: 'Cover of How to Read a Book by Mortimer J. Adler and Charles Van Doren',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'the-reading-mind',
        slug: 'the-reading-mind',
        title: 'The Reading Mind',
        author: 'Daniel T. Willingham',
        description:
            'A cognitive-science look at how the mind turns marks on a page into meaning. Selected for its research-grounded explanation of what actually happens when we read.',
        bestFor: 'Readers who want to understand the science behind comprehension.',
        categories: ['comprehension', 'reading-science'],
        audienceTags: ['Research-Minded Readers', 'Lifelong Learners'],
        badges: ['Research-Grounded', 'Reading Science'],
        coverSrc: '',
        coverAlt: 'Cover of The Reading Mind by Daniel T. Willingham',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'art-of-slow-reading',
        slug: 'the-art-of-slow-reading',
        title: 'The Art of Slow Reading',
        author: 'Thomas Newkirk',
        description:
            'A thoughtful case for slowing down to read more deeply, with practices for attention, rereading, and reflection. Recommended for readers who want balance against reading purely for speed.',
        bestFor: 'Readers who want to read deliberately and reflectively.',
        categories: ['comprehension'],
        audienceTags: ['Writers', 'Lifelong Learners', 'Research-Minded Readers'],
        badges: ['Deep Reading'],
        coverSrc: '',
        coverAlt: 'Cover of The Art of Slow Reading by Thomas Newkirk',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'reading-like-a-writer',
        slug: 'reading-like-a-writer',
        title: 'Reading Like a Writer',
        author: 'Francine Prose',
        description:
            'A close-reading guide that shows how attention to word, sentence, and structure deepens understanding. Selected for readers who want to read with a writer\u2019s eye.',
        bestFor: 'Writers and readers who want to notice craft while reading.',
        categories: ['comprehension'],
        audienceTags: ['Writers', 'Lifelong Learners'],
        badges: ['Deep Reading'],
        coverSrc: '',
        coverAlt: 'Cover of Reading Like a Writer by Francine Prose',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'well-educated-mind',
        slug: 'the-well-educated-mind',
        title: 'The Well-Educated Mind',
        author: 'Susan Wise Bauer',
        description:
            'A guide to giving yourself a classical education through reading, with methods for engaging serious books across genres. Recommended for self-directed, lifelong learners.',
        bestFor: 'Self-directed readers building a structured reading life.',
        categories: ['comprehension', 'learning'],
        audienceTags: ['Lifelong Learners', 'Research-Minded Readers'],
        badges: ['Deep Reading', 'Knowledge Building'],
        coverSrc: '',
        coverAlt: 'Cover of The Well-Educated Mind by Susan Wise Bauer',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },

    // =====================================================================
    // Memory and retention
    // =====================================================================
    {
        id: 'make-it-stick',
        slug: 'make-it-stick',
        title: 'Make It Stick',
        author: 'Peter C. Brown, Henry L. Roediger III, and Mark A. McDaniel',
        description:
            'A research-grounded account of what actually helps learning last, including retrieval practice and spaced repetition. Widely regarded as a foundational book on durable learning.',
        bestFor: 'Anyone who wants to remember more of what they study.',
        categories: ['memory', 'learning'],
        audienceTags: ['Students', 'Research-Minded Readers', 'Lifelong Learners'],
        badges: ['Research-Grounded', 'Memory Science'],
        coverSrc: '',
        coverAlt: 'Cover of Make It Stick by Peter C. Brown, Henry L. Roediger III, and Mark A. McDaniel',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'moonwalking-with-einstein',
        slug: 'moonwalking-with-einstein',
        title: 'Moonwalking with Einstein',
        author: 'Joshua Foer',
        description:
            'A narrative exploration of memory techniques and the world of competitive memorization. Recommended for readers who want an engaging introduction to how memory can be trained.',
        bestFor: 'Readers who want an entertaining route into memory technique.',
        categories: ['memory'],
        audienceTags: ['Lifelong Learners', 'Beginners'],
        badges: ['Accessible Introduction'],
        coverSrc: '',
        coverAlt: 'Cover of Moonwalking with Einstein by Joshua Foer',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'remember-genova',
        slug: 'remember-lisa-genova',
        title: 'Remember',
        author: 'Lisa Genova',
        description:
            'An accessible neuroscience explanation of how memory works, why we forget, and what supports remembering. Selected for its clear, science-based perspective.',
        bestFor: 'Readers who want to understand everyday memory and forgetting.',
        categories: ['memory', 'reading-science'],
        audienceTags: ['Lifelong Learners', 'Research-Minded Readers'],
        badges: ['Research-Grounded', 'Memory Science'],
        coverSrc: '',
        coverAlt: 'Cover of Remember by Lisa Genova',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'why-we-remember',
        slug: 'why-we-remember',
        title: 'Why We Remember',
        author: 'Charan Ranganath',
        description:
            'A neuroscientist\u2019s account of memory as an active, meaning-making process rather than simple storage. Recommended for readers interested in current memory research.',
        bestFor: 'Readers who want a modern, research-led view of memory.',
        categories: ['memory', 'reading-science'],
        audienceTags: ['Research-Minded Readers', 'Lifelong Learners'],
        badges: ['Research-Grounded', 'Memory Science'],
        coverSrc: '',
        coverAlt: 'Cover of Why We Remember by Charan Ranganath',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'your-memory-higbee',
        slug: 'your-memory-kenneth-higbee',
        title: 'Your Memory',
        author: 'Kenneth L. Higbee',
        description:
            'A practical guide to how memory works and how to improve it, balancing explanation with usable technique. Selected for its bridge between science and application.',
        bestFor: 'Readers who want both the why and the how of memory improvement.',
        categories: ['memory'],
        audienceTags: ['Students', 'Lifelong Learners'],
        badges: ['Practical Classic'],
        coverSrc: '',
        coverAlt: 'Cover of Your Memory by Kenneth L. Higbee',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'unlimited-memory',
        slug: 'unlimited-memory',
        title: 'Unlimited Memory',
        author: 'Kevin Horsley',
        description:
            'A concise, technique-focused guide to attention and memory methods for faster recall. Recommended for readers who want direct, applicable exercises.',
        bestFor: 'Readers who want practical memory techniques quickly.',
        categories: ['memory'],
        audienceTags: ['Students', 'Professionals'],
        badges: ['Beginner-Friendly'],
        coverSrc: '',
        coverAlt: 'Cover of Unlimited Memory by Kevin Horsley',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },

    // =====================================================================
    // Focus and attention
    // =====================================================================
    {
        id: 'peak-mind',
        slug: 'peak-mind',
        title: 'Peak Mind',
        author: 'Amishi P. Jha',
        description:
            'A neuroscientist\u2019s evidence-based approach to training attention and reducing mind-wandering. Widely regarded for translating attention research into practice.',
        bestFor: 'Readers who want to strengthen sustained attention.',
        categories: ['focus', 'reading-science'],
        audienceTags: ['Professionals', 'Research-Minded Readers', 'Lifelong Learners'],
        badges: ['Research-Grounded', 'Attention Science'],
        coverSrc: '',
        coverAlt: 'Cover of Peak Mind by Amishi P. Jha',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'deep-work',
        slug: 'deep-work',
        title: 'Deep Work',
        author: 'Cal Newport',
        description:
            'A widely read argument for cultivating distraction-free concentration and its value for meaningful work. Recommended for readers who want to protect focused reading and thinking time.',
        bestFor: 'Professionals who want to reclaim deep concentration.',
        categories: ['focus'],
        audienceTags: ['Professionals', 'Students'],
        badges: ['Influential Classic'],
        coverSrc: '',
        coverAlt: 'Cover of Deep Work by Cal Newport',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'indistractable',
        slug: 'indistractable',
        title: 'Indistractable',
        author: 'Nir Eyal',
        description:
            'A practical framework for understanding distraction and building habits that protect attention. Selected for its actionable take on managing digital pull.',
        bestFor: 'Readers who struggle with digital distraction.',
        categories: ['focus'],
        audienceTags: ['Professionals', 'Students', 'Lifelong Learners'],
        badges: ['Beginner-Friendly'],
        coverSrc: '',
        coverAlt: 'Cover of Indistractable by Nir Eyal',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'organized-mind',
        slug: 'the-organized-mind',
        title: 'The Organized Mind',
        author: 'Daniel J. Levitin',
        description:
            'A cognitive-science look at how attention and memory cope with information overload, with strategies for organizing thinking. Recommended for readers managing heavy information loads.',
        bestFor: 'Readers overwhelmed by information and decisions.',
        categories: ['focus', 'reading-science'],
        audienceTags: ['Professionals', 'Research-Minded Readers'],
        badges: ['Research-Grounded', 'Attention Science'],
        coverSrc: '',
        coverAlt: 'Cover of The Organized Mind by Daniel J. Levitin',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'digital-minimalism',
        slug: 'digital-minimalism',
        title: 'Digital Minimalism',
        author: 'Cal Newport',
        description:
            'A guide to being more intentional with technology so attention can go toward what matters, including reading. Recommended for readers rebuilding their attention span.',
        bestFor: 'Readers who want a healthier relationship with screens.',
        categories: ['focus'],
        audienceTags: ['Professionals', 'Lifelong Learners'],
        badges: ['Accessible Introduction'],
        coverSrc: '',
        coverAlt: 'Cover of Digital Minimalism by Cal Newport',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },

    // =====================================================================
    // Learning and studying
    // =====================================================================
    {
        id: 'outsmart-your-brain',
        slug: 'outsmart-your-brain',
        title: 'Outsmart Your Brain',
        author: 'Daniel T. Willingham',
        description:
            'A cognitive scientist\u2019s practical playbook for studying, reading, and learning more effectively. Selected for turning research into concrete study strategies.',
        bestFor: 'Students who want evidence-based study methods.',
        categories: ['learning'],
        audienceTags: ['Students', 'Research-Minded Readers'],
        badges: ['Research-Grounded', 'Student Essential'],
        coverSrc: '',
        coverAlt: 'Cover of Outsmart Your Brain by Daniel T. Willingham',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'how-we-learn-dehaene',
        slug: 'how-we-learn-stanislas-dehaene',
        title: 'How We Learn',
        author: 'Stanislas Dehaene',
        description:
            'A cognitive-neuroscience account of the mechanisms behind learning and why some methods work better than others. Recommended for readers interested in the science of learning.',
        bestFor: 'Research-minded readers who want depth on how learning works.',
        categories: ['learning', 'reading-science'],
        audienceTags: ['Research-Minded Readers', 'Lifelong Learners'],
        badges: ['Research-Grounded'],
        coverSrc: '',
        coverAlt: 'Cover of How We Learn by Stanislas Dehaene',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'mind-for-numbers',
        slug: 'a-mind-for-numbers',
        title: 'A Mind for Numbers',
        author: 'Barbara Oakley',
        description:
            'A guide to learning technical and difficult material, drawing on focused and diffuse thinking modes. Recommended for students facing challenging subjects.',
        bestFor: 'Students tackling math, science, or hard material.',
        categories: ['learning'],
        audienceTags: ['Students', 'Lifelong Learners'],
        badges: ['Student Essential', 'Beginner-Friendly'],
        coverSrc: '',
        coverAlt: 'Cover of A Mind for Numbers by Barbara Oakley',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'ultralearning',
        slug: 'ultralearning',
        title: 'Ultralearning',
        author: 'Scott H. Young',
        description:
            'A framework for aggressive, self-directed skill acquisition through intensive projects. Selected for readers who want to plan ambitious learning on their own terms.',
        bestFor: 'Self-directed learners pursuing hard skills quickly.',
        categories: ['learning'],
        audienceTags: ['Professionals', 'Lifelong Learners', 'Research-Minded Readers'],
        badges: ['Knowledge Building'],
        coverSrc: '',
        coverAlt: 'Cover of Ultralearning by Scott H. Young',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'how-we-learn-carey',
        slug: 'how-we-learn-benedict-carey',
        title: 'How We Learn',
        author: 'Benedict Carey',
        description:
            'A journalist\u2019s survey of learning research, covering spacing, testing, sleep, and context. Recommended as an approachable tour of what helps learning stick.',
        bestFor: 'Readers who want an accessible overview of learning science.',
        categories: ['learning'],
        audienceTags: ['Students', 'Lifelong Learners'],
        badges: ['Accessible Introduction'],
        coverSrc: '',
        coverAlt: 'Cover of How We Learn by Benedict Carey',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'little-book-of-talent',
        slug: 'the-little-book-of-talent',
        title: 'The Little Book of Talent',
        author: 'Daniel Coyle',
        description:
            'A compact collection of practical tips for skill development and deliberate practice. Selected for its short, usable prompts you can apply immediately.',
        bestFor: 'Readers who want quick, actionable practice tips.',
        categories: ['learning'],
        audienceTags: ['Students', 'Professionals', 'Lifelong Learners'],
        badges: ['Beginner-Friendly'],
        coverSrc: '',
        coverAlt: 'Cover of The Little Book of Talent by Daniel Coyle',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },

    // =====================================================================
    // Note-taking and knowledge management
    // =====================================================================
    {
        id: 'smart-notes',
        slug: 'how-to-take-smart-notes',
        title: 'How to Take Smart Notes',
        author: 'S\u00f6nke Ahrens',
        description:
            'A widely regarded guide to the slip-box (Zettelkasten) method for turning reading into durable, connected notes and original writing. Essential for building a personal knowledge system.',
        bestFor: 'Readers who want reading to feed lasting knowledge and writing.',
        categories: ['note-taking'],
        audienceTags: ['Writers', 'Research-Minded Readers', 'Students'],
        badges: ['Knowledge Building', 'Influential Classic'],
        coverSrc: '',
        coverAlt: 'Cover of How to Take Smart Notes by S\u00f6nke Ahrens',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'building-a-second-brain',
        slug: 'building-a-second-brain',
        title: 'Building a Second Brain',
        author: 'Tiago Forte',
        description:
            'A method for capturing, organizing, and using information across a lifetime of reading and work. Recommended for readers who want a practical digital knowledge workflow.',
        bestFor: 'Readers building a reliable personal knowledge workflow.',
        categories: ['note-taking'],
        audienceTags: ['Professionals', 'Lifelong Learners', 'Writers'],
        badges: ['Knowledge Building'],
        coverSrc: '',
        coverAlt: 'Cover of Building a Second Brain by Tiago Forte',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'para-method',
        slug: 'the-para-method',
        title: 'The PARA Method',
        author: 'Tiago Forte',
        description:
            'A concise system for organizing digital information by actionability. Selected for readers who want a simple, repeatable way to structure notes and files.',
        bestFor: 'Readers who want a lightweight organization system.',
        categories: ['note-taking'],
        audienceTags: ['Professionals', 'Lifelong Learners'],
        badges: ['Beginner-Friendly', 'Knowledge Building'],
        coverSrc: '',
        coverAlt: 'Cover of The PARA Method by Tiago Forte',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'bullet-journal-method',
        slug: 'the-bullet-journal-method',
        title: 'The Bullet Journal Method',
        author: 'Ryder Carroll',
        description:
            'A analog system for capturing tasks, notes, and reflections with intention. Recommended for readers who prefer a mindful, pen-and-paper approach to knowledge capture.',
        bestFor: 'Readers who prefer analog, reflective note-keeping.',
        categories: ['note-taking'],
        audienceTags: ['Lifelong Learners', 'Students'],
        badges: ['Accessible Introduction'],
        coverSrc: '',
        coverAlt: 'Cover of The Bullet Journal Method by Ryder Carroll',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },

    // =====================================================================
    // Reading science
    // =====================================================================
    {
        id: 'language-speed-of-sight',
        slug: 'language-at-the-speed-of-sight',
        title: 'Language at the Speed of Sight',
        author: 'Mark Seidenberg',
        description:
            'A researcher\u2019s examination of how reading works in the brain and what the science means for teaching and practice. Selected for its rigorous, evidence-based perspective.',
        bestFor: 'Readers who want a serious look at the science of reading.',
        categories: ['reading-science'],
        audienceTags: ['Research-Minded Readers'],
        badges: ['Research-Grounded', 'Reading Science'],
        coverSrc: '',
        coverAlt: 'Cover of Language at the Speed of Sight by Mark Seidenberg',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'reader-come-home',
        slug: 'reader-come-home',
        title: 'Reader, Come Home',
        author: 'Maryanne Wolf',
        description:
            'An exploration of how digital habits are reshaping the reading brain and what deep reading requires. Recommended for readers concerned about attention and deep reading.',
        bestFor: 'Readers who want to protect deep reading in a digital age.',
        categories: ['reading-science', 'focus'],
        audienceTags: ['Lifelong Learners', 'Research-Minded Readers'],
        badges: ['Reading Science', 'Deep Reading'],
        coverSrc: '',
        coverAlt: 'Cover of Reader, Come Home by Maryanne Wolf',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
    {
        id: 'proust-and-the-squid',
        slug: 'proust-and-the-squid',
        title: 'Proust and the Squid',
        author: 'Maryanne Wolf',
        description:
            'A study of how the human brain learned to read and what that reveals about reading development. Selected for its accessible narrative of reading science.',
        bestFor: 'Readers curious about how the reading brain developed.',
        categories: ['reading-science'],
        audienceTags: ['Lifelong Learners', 'Research-Minded Readers'],
        badges: ['Reading Science', 'Research-Grounded'],
        coverSrc: '',
        coverAlt: 'Cover of Proust and the Squid by Maryanne Wolf',
        status: 'published',
        isReaderFlexTitle: false,
        visible: false,
        isFeatured: false,
        amazonMarketplace: 'US',
    },
];

/**
 * Ordered book ids for the curated "Start Here" pathway. Each entry pairs a
 * book with the role it plays in the ReaderFLEX philosophy.
 */
export const START_HERE = [
    { bookId: 'breakthrough-rapid-reading', role: 'Reading efficiency', blurb: 'Build the mechanics of faster, more confident reading through deliberate practice.' },
    { bookId: 'how-to-read-a-book', role: 'Reading comprehension', blurb: 'Learn how to read at different depths so understanding scales with difficulty.' },
    { bookId: 'make-it-stick', role: 'Memory and retention', blurb: 'Understand what actually makes learning last, from retrieval to spacing.' },
    { bookId: 'peak-mind', role: 'Focus and attention', blurb: 'Train the sustained attention that every kind of reading depends on.' },
    { bookId: 'outsmart-your-brain', role: 'Learning', blurb: 'Turn reading into effective studying with evidence-based methods.' },
    { bookId: 'smart-notes', role: 'Note-taking and knowledge capture', blurb: 'Capture what you read so it becomes usable knowledge, not lost highlights.' },
];

// -------------------------------------------------------------------------
// Selectors
// -------------------------------------------------------------------------

export function getBookById(id) {
    return BOOKS.find((b) => b.id === id) || null;
}

export function getReaderFlexOriginals() {
    return BOOKS.filter((b) => b.isReaderFlexTitle).sort(
        (a, b) => (a.featuredRank || 999) - (b.featuredRank || 999)
    );
}

export function getThirdPartyBooks() {
    return BOOKS.filter((b) => !b.isReaderFlexTitle && b.visible !== false);
}

export function getAllThirdPartyBooks() {
    return BOOKS.filter((b) => !b.isReaderFlexTitle);
}

export function setBookVisibility(bookId, visible) {
    const book = BOOKS.find((b) => b.id === bookId);
    if (book) {
        book.visible = visible;
    }
}

export default BOOKS;
